import { useEffect, useMemo, useState } from 'react';

export interface RowSelectionState {
  readonly allKeys: ReadonlySet<string>;
  readonly selectedKeys: ReadonlySet<string>;
  set(rowId: string, select: boolean): void;
  toggle(rowId: string): void;
  setAll(selected: boolean): void;
  setKeys(selected: boolean, rowKeys: string[]): void;
  resetSelectedKeys: () => void;
}

export function useRowSelection<R>(
  rows: readonly R[] | undefined,
  getRowKey: (row: R) => string,
): RowSelectionState {
  const allKeys = useMemo(
    () => new Set(rows?.map((row) => getRowKey(row))),
    [rows, getRowKey],
  );

  const [selectedKeys, setSelectedKeys] = useState(new Set<string>());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, []);

  const { set, toggle, setAll, setKeys } = useMemo<
    Pick<RowSelectionState, 'set' | 'toggle' | 'setAll' | 'setKeys'>
  >(
    () => ({
      set(rowKey, select): void {
        if (!allKeys.has(rowKey)) {
          throw new Error(`Can't select key '${rowKey}'.`);
        }
        setSelectedKeys((prev) => {
          const result = new Set(prev);
          select ? result.add(rowKey) : result.delete(rowKey);
          return result;
        });
      },
      toggle(rowKey) {
        if (!allKeys.has(rowKey)) {
          throw new Error(`Can't select key '${rowKey}'.`);
        }
        setSelectedKeys((prev) => {
          const result = new Set(prev);
          if (result.has(rowKey)) {
            result.delete(rowKey);
          } else {
            result.add(rowKey);
          }
          return result;
        });
      },
      setAll(selected): void {
        setSelectedKeys(selected ? allKeys : new Set());
      },
      setKeys(selected, rowKeys): void {
        const keys = new Set(rowKeys);

        setSelectedKeys((prev) => {
          const result = new Set(prev);
          allKeys.forEach((key) => {
            if (keys.has(key)) {
              selected ? result.add(key) : result.delete(key);
            }
          });
          return result;
        });
      },
    }),
    [allKeys],
  );

  function resetSelectedKeys() {
    setSelectedKeys((prev) => (prev.size === 0 ? prev : new Set()));
  }

  return {
    allKeys,
    selectedKeys,
    set,
    toggle,
    setAll,
    setKeys,
    resetSelectedKeys,
  };
}
